// Audiotool's own render of a project — the most faithful audio there is.
//
// The render runs on Audiotool's servers with the real instruments, effects,
// mixer and time-stretching, so it is what the producer actually hears in the
// Studio. It is NOT on the SDK's client (@audiotool/nexus 0.0.17); the calls
// are made here, and were checked against the live service
// (tools/audiotool-render-spike.ts, 2026-09-25):
//
//   • DocumentService.RenderAudio / GetCommitIndexRange live on the project's
//     own document server — the URL projects.openSession names;
//   • the render is a long-running operation polled on the PUBLIC API's
//     OperationService (rpc.audiotool.com) — but an OAuth app's token is
//     refused there ("permission_denied: insufficient rights"; a personal
//     token isn't), so without it we wait for the file at its CDN URL, which
//     the operation's name and the commit determine;
//   • its result is `audiotool.projectaudio.v1.RenderResult` — a duration and a
//     format → URL map (hls, mp3, ogg, wav) on cdn.audiotool.com;
//   • every file is padded to a fixed length (204 s observed), so callers cut
//     it to the arrangement;
//   • a render of a COMMIT renders the project as it stood then, whatever has
//     happened since — which is what makes per-track renders possible without
//     leaving the producer's project changed (./track-renders.ts).
//
// Wire: the Connect protocol's JSON form over plain `fetch`, with plain-object
// headers and `res.json()`. No protobuf runtime and no Connect client: Live's
// extension host has no Headers, TextDecoder or Blob globals (auth/http.ts,
// uploads.ts), and this same file runs there, in Node, and in a tab. Every
// Audiotool host involved answers JSON and is CORS-open. Not the SDK client's
// `authorizedFetch`: in 0.0.17 it sends no token at all.

import type { AudiotoolClient } from "@audiotool/nexus";

import { errorText, isSdkError } from "./uploads.js";

const PUBLIC_API = "https://rpc.audiotool.com";
const RENDER_CDN = "https://cdn.audiotool.com/projectaudio/render";
/**
 * Bytes a second of render can take: 32-bit float stereo at 48 kHz (observed:
 * float stereo at 44.1 kHz). A truncated WAV still decodes — the reader clamps
 * the data chunk to what arrived.
 */
const MAX_BYTES_PER_SECOND = 48_000 * 2 * 4;
const WAV_HEADER_ALLOWANCE = 4096;
const POLL_MS = 2000;
const TIMEOUT_MS = 5 * 60_000;

/** A long-running operation, as the Connect JSON wire carries it. */
export interface OperationView {
  name: string;
  done?: boolean;
  error?: { code?: number | string; message?: string };
  /** A packed Any: `@type`, then the message's own fields inline. */
  response?: { "@type"?: string; downloadUrls?: Record<string, string> };
}

/** A Connect error; `code` is the protocol's name for it ("permission_denied"). */
export class AudiotoolRpcError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(`[${code}] ${message}`);
    this.name = "AudiotoolRpcError";
  }
}

export interface RenderClient extends Pick<AudiotoolClient, "projects"> {
  /** The user's current access token, with or without its "Bearer " prefix. */
  getToken: () => Promise<string>;
}

/** How this environment talks to Audiotool's RPC hosts — overridable in tests. */
export interface RenderApi {
  renderAudio(projectName: string, commitIndex: bigint): Promise<OperationView>;
  getOperation(name: string): Promise<OperationView>;
  latestCommit(projectName: string): Promise<bigint>;
  /** The file, or only its first `maxBytes` when given (an HTTP range). */
  download(url: string, maxBytes?: number): Promise<Uint8Array>;
}

export interface RenderedProject {
  /** The rendered WAV, padded past the arrangement — cut it to length. */
  wav: Uint8Array;
  /** The commit that was rendered (read back from the download URL when present). */
  commitIndex: bigint | null;
}

/** The real API: Connect JSON over `fetch`, as the signed-in user. */
export function renderApi(client: RenderClient, fetchFn: typeof fetch = (u, i) => fetch(u, i)): RenderApi {
  const call = async <T>(base: string, method: string, body: unknown): Promise<T> => {
    const token = await client.getToken();
    const res = await fetchFn(`${base}/${method}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Connect-Protocol-Version": "1",
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      credentials: "omit",
    });
    const json = (await res.json().catch(() => null)) as (T & { code?: string; message?: string }) | null;
    if (!res.ok) throw new AudiotoolRpcError(json?.code ?? `http_${res.status}`, json?.message ?? `HTTP ${res.status}`);
    return json as T;
  };

  const docServers = new Map<string, string>();
  const docServer = async (projectName: string): Promise<string> => {
    const hit = docServers.get(projectName);
    if (hit) return hit;
    const s = await client.projects.openSession({ projectName });
    if (isSdkError(s)) throw new Error(`Couldn't reach the project's document server: ${errorText(s)}`);
    const url = s.session?.documentServiceUrl;
    if (!url) throw new Error("Audiotool returned no document server for the project.");
    const base = url.replace(/\/+$/, "");
    docServers.set(projectName, base);
    return base;
  };

  return {
    async renderAudio(projectName, commitIndex) {
      return call<OperationView>(await docServer(projectName), "audiotool.document.v1.DocumentService/RenderAudio", {
        projectName,
        commitIndex: String(commitIndex),
      });
    },
    async getOperation(name) {
      const r = await call<{ operation?: OperationView }>(PUBLIC_API, "audiotool.longrunning.v1.OperationService/GetOperation", { name });
      if (!r.operation) throw new Error("Audiotool returned no operation.");
      return r.operation;
    },
    async latestCommit(projectName) {
      const r = await call<{ maxCommitIndex?: string }>(
        await docServer(projectName),
        "audiotool.document.v1.DocumentService/GetCommitIndexRange",
        { projectName },
      );
      return BigInt(r.maxCommitIndex ?? "0");
    },
    async download(url, maxBytes) {
      // The CDN URLs are public (and CORS-open); no token rides along. A range
      // saves pulling a 204 s render for a 30 s song.
      const res = await fetchFn(url, maxBytes ? { headers: { Range: `bytes=0-${maxBytes - 1}` } } : {});
      if (!res.ok) throw new Error(`Audiotool's render download failed (HTTP ${res.status}).`);
      return new Uint8Array(await res.arrayBuffer());
    },
  };
}

/**
 * Render `projectName` at `commitIndex` (0n: the latest) and download the WAV.
 * Polls until done; throws on a failed render or after five minutes.
 */
export async function renderProject(
  api: RenderApi,
  projectName: string,
  commitIndex: bigint,
  opts: { signal?: AbortSignal; pollMs?: number; timeoutMs?: number; maxSeconds?: number } = {},
): Promise<RenderedProject> {
  const t0 = Date.now();
  const maxBytes = opts.maxSeconds ? WAV_HEADER_ALLOWANCE + Math.ceil(opts.maxSeconds * MAX_BYTES_PER_SECOND) : undefined;
  // "Latest" is resolved up front: the CDN fallback below needs the number.
  const commit = commitIndex === 0n ? await api.latestCommit(projectName) : commitIndex;
  let op = await api.renderAudio(projectName, commit);
  const wait = async (): Promise<void> => {
    if (opts.signal?.aborted) throw new Error("Render cancelled.");
    if (Date.now() - t0 > (opts.timeoutMs ?? TIMEOUT_MS)) throw new Error("Audiotool's render took too long.");
    await new Promise((r) => setTimeout(r, opts.pollMs ?? POLL_MS));
  };
  while (!op.done) {
    await wait();
    try {
      op = await api.getOperation(op.name);
    } catch (e) {
      // An OAuth app's token may start a render but not read the operation
      // ("permission_denied: insufficient rights" — only a personal token
      // may). The file lands at a URL named by the operation and the commit;
      // wait for it there instead.
      if (!isPermissionDenied(e)) throw e;
      const url = renderFileUrl(op.name, commit);
      if (!url) throw e;
      for (;;) {
        try {
          return { wav: await api.download(url, maxBytes), commitIndex: commit };
        } catch {
          await wait();
        }
      }
    }
  }
  if (op.error) throw new Error(`Audiotool couldn't render the project: ${op.error.message || `code ${String(op.error.code)}`}`);
  const urls = op.response?.downloadUrls;
  if (!urls) throw new Error("Audiotool's render finished with no result.");
  const url = urls["wav"];
  if (!url) throw new Error(`Audiotool's render offered no WAV (only ${Object.keys(urls).join(", ")}).`);
  return { wav: await api.download(url, maxBytes), commitIndex: commitOfUrl(url) };
}

/**
 * Where a render's WAV lands: `…/projectaudio/render/<render id>/<commit>/track.wav`,
 * the render id being the one in the operation's name (`renders/<id>/operations/…`)
 * — observed on every render so far. Null for an operation name of another shape.
 */
export function renderFileUrl(operationName: string, commit: bigint): string | null {
  const m = /^renders\/([^/]+)\/operations\//.exec(operationName);
  return m ? `${RENDER_CDN}/${m[1]}/${String(commit)}/track.wav` : null;
}

function isPermissionDenied(e: unknown): boolean {
  const code = (e as { code?: unknown } | null)?.code;
  return code === "permission_denied" || code === 7 || /permission_denied|insufficient rights/i.test(e instanceof Error ? e.message : String(e));
}

/** `…/render/<id>/<commit>/track.wav` → the commit, when the URL has that shape. */
export function commitOfUrl(url: string): bigint | null {
  const m = /\/(\d+)\/[^/?#]+(?:[?#].*)?$/.exec(url);
  return m ? BigInt(m[1]!) : null;
}
